import qs from "qs";
import moment from "moment";
import { useHistory } from "react-router";
import { Flex } from "rebass/styled-components";
import React, { useCallback, useEffect, useState } from "react";
import { useToaster } from "@hellocontento/maillard";
import { useSelector, useDispatch } from "react-redux";

import {
  ItemText,
  ItemTitle,
  ItemDomain,
  CoverTitle,
  ItemNewDot,
  ActionZone,
  CoverDomain,
  ItemFavicon,
  ContentItemCover,
  ContentIconButton,
  ContentItemWrapper,
  SuggestedItemContent,
  ContentIconButtonIcon,
  ContentScheduleButton,
  ContentIconButtonGroup,
  ContentItemDateWrapper
} from "./styles";
import {
  likeCounts,
  likeContent,
  blockDomain,
  saveContent,
  unlikeContent,
  unsaveContent,
  dismissContent
} from "services/news";
import {
  useComposerState,
  useComposerActions
} from "contextApi/composerContext";
import Publisher from "./common/Publisher";
import { getRelativeDate } from "utils/date";
import Thumbnail from "./contentItem/Thumbnail";
import { openModal } from "state/actions/ModalActions";
import { ToolTip } from "components/schedule/common/styles";
import ContentDismissReasons from "./ContentDismissReasons";
import { OnboardingStepsEnum } from "types/Onboarding.types";
import ContentItemFooter from "./contentItem/ContentItemFooter";
import { trackAnalyticsEvent } from "state/actions/AnalyticsActions";
import ArticlePreviewModal from "./articlePreviewModal/ArticlePreviewModal";

const getContentItemDate = content =>
  getRelativeDate(content.published_date || content.updated_at);

const Favicon = React.memo(({ url }) => {
  const [hasError, setError] = useState(false);

  return (
    <>
      {url && !hasError && (
        <ItemFavicon
          src={`${url}`}
          hasError={hasError}
          onError={() => setError(true)}
        />
      )}
    </>
  );
});

const ContentItem = React.memo(props => {
  const {
    content,
    viewOnly,
    sourceId,
    onDismiss,
    onSaveChange,
    isNew = false,
    variant = "regular",
    enableDismiss = true,
    showDescription = false,
    tooltipPlacement = "top"
  } = props;
  const {
    id,
    url,
    logo,
    title,
    domain,
    article,
    favicon,
    computed,
    thumbnail,
    publisher,
    description,
    captions = []
  } = content;

  const dispatch = useDispatch();
  const account = useSelector(state => state.account.data);
  const currentUser = useSelector(state => state.auth.currentUser);
  const [showPreview, setShowPreview] = useState(false);
  const [actionsVisible, setActionsVisible] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [wasSaved, setWasSaved] = useState(!!computed?.isSaved);
  const [wasLiked, setWasLiked] = useState(!!computed?.isLiked);
  const [isDismissVisible, setDismissVisible] = useState(false);
  const onPostedEvent = useComposerState(state => state.events.onPostedEvent);
  const openComposer = useComposerActions(actions => actions.openComposer);

  const [onHover, setOnHover] = useState(false);

  const [wasPosted, setWasPosted] = useState(computed?.isPosted || false);

  const addToast = useToaster();
  const history = useHistory();

  const newsType =
    history?.location.pathname.match(
      /^.*\/accounts\/[^/]*\/content\/([^/?]*).*$/
    )?.[1] ?? "";

  const handleArticlePreview = useCallback(status => {
    document.body.style.overflow = status ? "hidden" : "unset";
    setShowPreview(status);
  }, []);

  const handleScheduleClick = useCallback(() => {
    const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    handleArticlePreview(false);
    openComposer(null, {
      initialContent: `${title.trim()} ${url}`,
      initialContentTypeId: "educational",
      contentTypeLabel: "News Article",
      initialScheduleTime: query.date
        ? moment.tz(query.date, account.timezone)
        : undefined,
      initialChannelIds: query.channelIds ? query.channelIds : undefined,
      content,
      contentId: id,
      captions: [title.trim(), ...captions]
    });
  }, [
    id,
    url,
    title,
    content,
    captions,
    openComposer,
    account.timezone,
    handleArticlePreview
  ]);

  const onPosted = useCallback(() => {
    setIsChanging(false);
    setWasPosted(true);
  }, []);

  useEffect(() => onPostedEvent.listen(onPosted), [onPostedEvent, onPosted]);

  const handleDismiss = useCallback(
    async (_, reason, details) => {
      setIsChanging(true);
      try {
        if (reason === "mute_domain") {
          await blockDomain({
            domain: details.domain,
            name: details.name,
            image: details.image
          });
          onDismiss(content.id, details.domain);
        } else {
          await dismissContent(content.id, {
            data: {
              reason: reason,
              dismissed_by: currentUser.id,
              details: details
            }
          });
          onDismiss(content.id);
        }
        dispatch(
          trackAnalyticsEvent("Article Voted On", {
            voteType: "Disliked",
            reason
          })
        );
        handleArticlePreview(false);
        setDismissVisible(false);
        setIsChanging(false);
        addToast("Thanks. We'll tune your recommendations", "info");
      } catch (e) {
        setDismissVisible(false);
        setIsChanging(false);
      }
    },
    [
      addToast,
      dispatch,
      onDismiss,
      content.id,
      currentUser.id,
      handleArticlePreview
    ]
  );

  const handleSaveClick = useCallback(async () => {
    const savedArticlesPath = `/accounts/${account.id}/content/saved-for-later`;
    try {
      if (wasSaved) {
        setWasSaved(false);
        await unsaveContent(content, newsType);
        if (onSaveChange) onSaveChange(false, id);
      } else {
        setWasSaved(true);
        await saveContent(content, newsType);

        if (onSaveChange) onSaveChange(true, id);

        addToast("Added to favorites", "success", {
          label: "View",
          action: () => history.push(savedArticlesPath)
        });
      }
    } catch (err) {
      setWasSaved(wasSaved);
    }
  }, [
    id,
    content,
    history,
    addToast,
    wasSaved,
    newsType,
    account.id,
    onSaveChange
  ]);

  const handleLikeContent = useCallback(async () => {
    setWasLiked(!wasLiked);
    try {
      if (wasLiked) {
        await unlikeContent(id);
      } else {
        await likeContent(id);
        if (
          !account.onboardingProgress.includes(OnboardingStepsEnum.LIKE_ARTICLE)
        ) {
          likeCounts().then(counts => {
            if (counts.length >= 3) {
              dispatch(
                openModal("ONBOARDING_INFO_MODAL", {
                  id: OnboardingStepsEnum.LIKE_ARTICLE,
                  triggeredBy: OnboardingStepsEnum.LIKE_ARTICLE
                })
              );
            }
          });
        }

        addToast("Thank you! We will tune your recommendations.", "success");
      }
    } catch (_) {
      setWasLiked(!wasLiked);
    }
  }, [account.onboardingProgress, addToast, dispatch, id, wasLiked]);

  const navigateToDomain = useCallback(() => {
    const { domain: contentDomain } = computed;

    const domainLink =
      contentDomain && contentDomain.id
        ? `/accounts/${account.id}/content/domains/${contentDomain.id}`
        : null;

    if (domainLink) history.push(domainLink);
  }, [account.id, computed, history]);

  const text = description || article;

  const ContentItemActions = React.memo(() => (
    <ActionZone isVisible={actionsVisible}>
      <ContentIconButtonGroup px={10} py={10}>
        <ContentIconButton
          active={wasLiked}
          disabled={isChanging}
          onClick={handleLikeContent}
          data-tip
          data-for="like-icon"
        >
          <ContentIconButtonIcon
            className={`${wasLiked ? "icon-like-filled-24" : "icon-like-24"}`}
          />
        </ContentIconButton>
        <ToolTip id="like-icon" place={tooltipPlacement}>
          More of this
        </ToolTip>
        {enableDismiss && (
          <>
            <ContentIconButton
              onClick={() => setDismissVisible(true)}
              disabled={isChanging}
              data-tip
              data-for="dismiss-icon"
            >
              <ContentIconButtonIcon className="icon-disliked-24" />
            </ContentIconButton>
            <ToolTip id="dismiss-icon" place={tooltipPlacement}>
              Less of this
            </ToolTip>
          </>
        )}
        <ContentIconButton
          saved={wasSaved}
          onClick={handleSaveClick}
          data-tip
          data-for="bookmark-icon"
        >
          <ContentIconButtonIcon
            className={
              wasSaved ? `icon-bookmark-filled-24` : `icon-bookmark-24`
            }
          />
        </ContentIconButton>
        <ToolTip id="bookmark-icon" place={tooltipPlacement}>
          Bookmark
        </ToolTip>
        <ContentIconButton
          onClick={() => handleArticlePreview(true)}
          data-tip
          data-for="read-icon"
        >
          <span className="icon icon-read" />
        </ContentIconButton>
        <ToolTip id="read-icon" place={tooltipPlacement}>
          Reader view
        </ToolTip>
      </ContentIconButtonGroup>
      <Flex justifyContent="center">
        <ContentScheduleButton
          onClick={handleScheduleClick}
          disabled={isChanging}
        >
          <span className="icon-send-filled" />
          Post now
        </ContentScheduleButton>
      </Flex>
      <Flex py={20} />
    </ActionZone>
  ));

  if (variant === "cover")
    return (
      <ContentItemWrapper>
        {isDismissVisible ? (
          <ContentDismissReasons
            domain={domain}
            image={favicon || logo}
            name={publisher}
            topic={null}
            onSubmit={handleDismiss}
            onCancel={() => setDismissVisible(false)}
          />
        ) : (
          <ContentItemCover
            onMouseEnter={() => setActionsVisible(true)}
            onMouseLeave={() => setActionsVisible(false)}
            thumbnail={thumbnail}
          >
            {!viewOnly && <ContentItemActions />}
            <SuggestedItemContent variant="cover">
              <Flex paddingY={20}>
                <Favicon url={favicon || logo} />
                <CoverDomain>
                  {!publisher ? domain : publisher} ·{" "}
                  {getContentItemDate(content)}
                </CoverDomain>
              </Flex>
              <CoverTitle href={url} rel={"nofollow"} target={"_blank"}>
                {title}
              </CoverTitle>
            </SuggestedItemContent>
          </ContentItemCover>
        )}
      </ContentItemWrapper>
    );
  else if (props.orientation === "landscape") {
    return (
      <ContentItemWrapper orientation={"landscape"} isChanging={isChanging}>
        {isDismissVisible ? (
          <ContentDismissReasons
            domain={domain}
            image={favicon || logo}
            name={publisher}
            topic={null}
            onSubmit={handleDismiss}
            onCancel={() => setDismissVisible(false)}
          />
        ) : (
          <>
            <Thumbnail
              src={thumbnail}
              size={variant === "regular" ? "md" : "sm"}
              onMouseEnter={() => setActionsVisible(true)}
              onMouseLeave={() => setActionsVisible(false)}
            >
              {!viewOnly && <ContentItemActions />}
            </Thumbnail>
            <SuggestedItemContent orientation={"landscape"}>
              <Flex flexDirection={"column"}>
                <Flex>
                  <Favicon url={favicon || logo} />
                  <ItemDomain>
                    <Publisher
                      hasDomain={computed && computed?.domain}
                      text={!publisher ? domain : publisher}
                      onClick={navigateToDomain}
                    />
                    <ContentItemDateWrapper>
                      {getContentItemDate(content, sourceId)}
                    </ContentItemDateWrapper>
                  </ItemDomain>
                </Flex>
                <ItemTitle
                  size={variant === "regular" ? "md" : "sm"}
                  href={url}
                  rel={"nofollow"}
                  target={"_blank"}
                  showDescription={showDescription}
                >
                  {isNew && <ItemNewDot />} {title}
                </ItemTitle>
                {showDescription && <ItemText>{text}</ItemText>}
              </Flex>
              <ContentItemFooter
                id={id}
                orientation={props.orientation}
                viewOnly={viewOnly}
                wasSaved={wasSaved}
                wasLiked={wasLiked}
                wasPosted={wasPosted}
                onTagClickNavigateTo={""}
                handleDismiss={handleDismiss}
                handleSaveClick={handleSaveClick}
                handleLikeContent={handleLikeContent}
                handleScheduleClick={handleScheduleClick}
                date={getContentItemDate(content, sourceId)}
                handlePreviewArticle={() => setShowPreview(true)}
                predicted_tags={
                  computed?.topics?.map(topic => ({
                    id: topic.id,
                    title: topic.title,
                    lang: topic.language
                  })) ?? []
                }
              />
            </SuggestedItemContent>
            {showPreview && (
              <ArticlePreviewModal
                content={content}
                onSchedule={() => handleScheduleClick()}
                onDismiss={handleDismiss}
                onSave={handleSaveClick}
                saved={wasSaved}
                closeModal={() => handleArticlePreview(false)}
              />
            )}
          </>
        )}
      </ContentItemWrapper>
    );
  } else
    return (
      <ContentItemWrapper
        orientation={props.orientation ?? "portrait"}
        isChanging={isChanging}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
      >
        {isDismissVisible ? (
          <ContentDismissReasons
            domain={domain}
            image={favicon || logo}
            name={publisher}
            topic={null}
            onSubmit={handleDismiss}
            onCancel={() => setDismissVisible(false)}
          />
        ) : (
          <>
            <Flex flexDirection={"column"} justifyContent={"flex-start"}>
              <Thumbnail
                src={thumbnail}
                size={variant === "regular" ? "md" : "sm"}
                thumbnailHeight={props.thumbnailHeight}
                onMouseEnter={() => setActionsVisible(true)}
                onMouseLeave={() => setActionsVisible(false)}
              >
                {!viewOnly && <ContentItemActions />}
              </Thumbnail>
              <SuggestedItemContent>
                <Flex>
                  <Favicon url={favicon || logo} />
                  <ItemDomain>
                    <Publisher
                      hasDomain={computed && computed?.domain}
                      text={!publisher ? domain : publisher}
                      onClick={navigateToDomain}
                    />
                    <ContentItemDateWrapper>
                      {getContentItemDate(content, sourceId)}
                    </ContentItemDateWrapper>
                  </ItemDomain>
                </Flex>
                <ItemTitle
                  size={variant === "regular" ? "md" : "sm"}
                  href={url}
                  rel={"nofollow"}
                  target={"_blank"}
                  showDescription={showDescription}
                >
                  {isNew && <ItemNewDot />} {title}
                </ItemTitle>
                {showDescription && <ItemText>{text}</ItemText>}
              </SuggestedItemContent>
            </Flex>
            <ContentItemFooter
              id={id}
              onHover={onHover}
              viewOnly={viewOnly}
              wasSaved={wasSaved}
              wasLiked={wasLiked}
              wasPosted={wasPosted}
              onTagClickNavigateTo={""}
              handleDismiss={handleDismiss}
              handleSaveClick={handleSaveClick}
              handleLikeContent={handleLikeContent}
              handleScheduleClick={handleScheduleClick}
              handlePreviewArticle={() => setShowPreview(true)}
              date={getContentItemDate(content, sourceId)}
              predicted_tags={
                computed?.topics?.map(topic => ({
                  id: topic.id,
                  title: topic.title,
                  lang: topic.language
                })) ?? []
              }
            />
            {showPreview && (
              <ArticlePreviewModal
                content={content}
                onSchedule={() => handleScheduleClick()}
                onDismiss={handleDismiss}
                onSave={handleSaveClick}
                saved={wasSaved}
                closeModal={() => handleArticlePreview(false)}
              />
            )}
          </>
        )}
      </ContentItemWrapper>
    );
});

export default ContentItem;
